import { PublisherInterface } from '@chili-publish/publisher-interface'
import { useEffect, useRef, useState } from 'react'

import {
  CursorProperty,
  CursorTypes
} from 'shared/components/Tools/services/types'

const ZOOM_STEP = 10
const ZOOM_STEP_PERCENT = ZOOM_STEP / 100
const MAX_ZOOM_STEP_COUNTER = 20
const INITIAL_ZOOM_LABEL = 100
const MIN_ZOOM_STEP_COUNTER = 5

export const useEditorZoom = ({
  publisherConnector,
  isWorkspaceRendered,
  initialZoomValue
}: {
  publisherConnector: PublisherInterface
  isWorkspaceRendered: boolean
  initialZoomValue?: number
}) => {
  const [currentZoomLabel, setCurrentZoomLabel] = useState(
    initialZoomValue || INITIAL_ZOOM_LABEL
  )
  const [currentZoomValue, setCurrentZoomValue] = useState<
    null | number
  >(null)
  const initialZoom = useRef<null | number>(null)
  const [cursorType, setCursorType] = useState<CursorTypes | null>(
    null
  )

  useEffect(() => {
    const init = async () => {
      if (isWorkspaceRendered && publisherConnector) {
        publisherConnector.getProperty('cursor').then(cursor => {
          const cursorData = cursor as CursorProperty
          if (cursor) {
            setCursorType(cursorData?.name || null)
          }
        })
  
        if (initialZoomValue) {
          await publisherConnector?.setProperty(
            'document',
            'zoom',
            initialZoomValue
          )
          initialZoom.current = initialZoomValue
        }
  
        publisherConnector.getProperty('zoom').then(zoom => {
          initialZoom.current = typeof zoom === 'number' ? zoom : null
          setCurrentZoomValue(initialZoom.current)
        })
      }
    }

    init()
  }, [isWorkspaceRendered, publisherConnector, setCursorType])

  const onZoomIn = async () => {
    if (!currentZoomValue || !initialZoom.current) {
      return
    }

    const maxZoomValue =
      initialZoom.current +
      initialZoom.current * ZOOM_STEP_PERCENT * MAX_ZOOM_STEP_COUNTER
    const newZoomValue =
      currentZoomValue + initialZoom.current * ZOOM_STEP_PERCENT
    const finalZoomValue = Math.min(newZoomValue, maxZoomValue)

    const maxValueLabel =
      INITIAL_ZOOM_LABEL + MAX_ZOOM_STEP_COUNTER * ZOOM_STEP
    const newZoomLabel = currentZoomLabel + ZOOM_STEP
    const finalZoomLabel = Math.min(newZoomLabel, maxValueLabel)

    setCurrentZoomLabel(finalZoomLabel)
    setCurrentZoomValue(finalZoomValue)

    await publisherConnector?.setProperty(
      'document',
      'zoom',
      finalZoomValue
    )
  }

  const onZoomOut = async () => {
    if (!currentZoomValue || !initialZoom.current) {
      return
    }

    const minZoomValue =
      initialZoom.current -
      initialZoom.current * ZOOM_STEP_PERCENT * MIN_ZOOM_STEP_COUNTER

    const newZoomValue =
      currentZoomValue - initialZoom.current * ZOOM_STEP_PERCENT
    const finalZoomValue = Math.max(newZoomValue, minZoomValue)

    const minValueLabel =
      INITIAL_ZOOM_LABEL - MIN_ZOOM_STEP_COUNTER * ZOOM_STEP
    const newZoomLabel = currentZoomLabel - ZOOM_STEP
    const finalZoomLabel = Math.max(newZoomLabel, minValueLabel)

    setCurrentZoomLabel(finalZoomLabel)
    setCurrentZoomValue(finalZoomValue)
    await publisherConnector?.setProperty(
      'document',
      'zoom',
      finalZoomValue
    )
  }

  const onZoomFit = async () => {
    if (!currentZoomValue || !initialZoom.current) {
      return
    }

    setCurrentZoomLabel(100)
    setCurrentZoomValue(initialZoom.current)
    await publisherConnector?.executeFunction(
      'document.editor',
      'Fit',
      'page'
    )
  }

  const onMoveToggle = async () => {
    if (cursorType === 'pointer') {
      await publisherConnector?.executeFunction(
        'document',
        'SetCursor',
        'hand'
      )
      setCursorType('hand')
    } else {
      await publisherConnector?.executeFunction(
        'document',
        'SetCursor',
        'pointer'
      )
      setCursorType('pointer')
    }
  }

  return {
    onZoomIn,
    onZoomOut,
    onZoomFit,
    onMoveToggle,
    initialZoom,
    currentZoomLabel,
    currentZoomValue,
    cursorType
  }
}
